import {useContext} from 'react';
import React from 'react';
import {defineMessages, useIntl} from 'react-intl';
import type {ButtonColumn, CurrencyColumn, DataGridColumn, DataGridServerProps, TextColumn} from '@igp/shared/components/data-grid';
import {DataGridServer} from '@igp/shared/components/data-grid';
import {LocalizedText} from '@igp/shared/components/i18n';
import {ModalContext} from '@igp/shared/components/modal';
import {formatTimestamp} from '@igp/shared/utils';

import {repaymentLogLocalizedHeaders} from '../localized';
import type {RepaymentLogColumns, RepaymentLogViewModel} from '../types';

import {RemoveRepaymentLogConfirmationModal} from './ConfirmationModal';
import {EditRepaymentLogForm} from './EditForm';

const localized = defineMessages({
    editTitle: {
        id: 'RepaymentLogDataGrid_editTitle',
        defaultMessage: 'Edit Repayment Entry',
    },
    removeConfirmation: {
        id: 'RepaymentLogDataGrid_removeConfirmation',
        defaultMessage: 'Are you sure you want to delete this repayment entry?',
    },
});

function useRepaymentLogsColumns(columns: RepaymentLogColumns[]) {
    const currency = 'USD';
    const {formatMessage} = useIntl();
    const {openModal} = useContext(ModalContext);

    const defaultColumn: Partial<DataGridColumn<RepaymentLogViewModel>> = {
        width: 200,
        sortable: true,
    };

    const repaymentAmount: CurrencyColumn<RepaymentLogViewModel> = {
        ...defaultColumn,
        type: 'currency',
        field: 'repaymentAmount',
        title: repaymentLogLocalizedHeaders.repaymentAmount,
        valueGetter: vm => ({amount: vm.repaymentAmount, currency}),
    };

    const repaymentDate: TextColumn<RepaymentLogViewModel> = {
        ...defaultColumn,
        type: 'text',
        field: 'repaymentDate',
        title: repaymentLogLocalizedHeaders.repaymentDate,
        valueGetter: vm => formatTimestamp(vm.repaymentDate, 'date'),
    };

    const fromAgentUid: TextColumn<RepaymentLogViewModel> = {
        ...defaultColumn,
        type: 'text',
        field: 'fromAgentUid',
        title: repaymentLogLocalizedHeaders.fromAgentUid,
        valueGetter: vm => vm.fromAgentUid,
    };

    const fromAgentUsername: TextColumn<RepaymentLogViewModel> = {
        ...defaultColumn,
        type: 'text',
        field: 'fromAgentUsername',
        title: repaymentLogLocalizedHeaders.fromAgentUsername,
        valueGetter: vm => vm.fromAgentUsername,
    };

    const masterAgentUid: TextColumn<RepaymentLogViewModel> = {
        ...defaultColumn,
        type: 'text',
        field: 'masterAgentUid',
        title: repaymentLogLocalizedHeaders.masterAgentUid,
        valueGetter: vm => vm.masterAgentUid,
    };

    const masterAgentUsername: TextColumn<RepaymentLogViewModel> = {
        ...defaultColumn,
        type: 'text',
        field: 'masterAgentUsername',
        title: repaymentLogLocalizedHeaders.masterAgentUsername,
        valueGetter: vm => vm.masterAgentUsername,
        width: 250,
    };

    const edit: ButtonColumn<RepaymentLogViewModel> = {
        type: 'button',
        field: 'edit',
        valueGetter: vm => ({
            title: formatMessage(repaymentLogLocalizedHeaders.edit),
            onClick: () =>
                openModal({
                    title: <LocalizedText label={localized.editTitle} />,
                    body: <EditRepaymentLogForm log={vm} />,
                }),
        }),
        sortable: false,
        width: 100,
    };

    const remove: ButtonColumn<RepaymentLogViewModel> = {
        type: 'button',
        field: 'remove',
        valueGetter: vm => ({
            title: formatMessage(repaymentLogLocalizedHeaders.remove),
            onClick: () =>
                openModal({
                    title: <LocalizedText label={localized.removeConfirmation} />,
                    body: <RemoveRepaymentLogConfirmationModal log={vm} />,
                }),
        }),
        sortable: false,
        width: 100,
    };

    const config: Record<RepaymentLogColumns, DataGridColumn<RepaymentLogViewModel>> = {
        repaymentAmount,
        repaymentDate,
        fromAgentUid,
        fromAgentUsername,
        masterAgentUid,
        masterAgentUsername,
        edit,
        remove,
    };

    return columns.map(column => config[column]);
}
type RepaymentLogDataGridProps = Omit<DataGridServerProps<RepaymentLogViewModel, RepaymentLogColumns>, 'columns'> & {
    columns: RepaymentLogColumns[];
};

export function RepaymentLogDataGrid({columns, ...props}: RepaymentLogDataGridProps) {
    const configuredColumns = useRepaymentLogsColumns(columns);
    return <DataGridServer {...props} columns={configuredColumns} />;
}
